import { booleanAttribute, computed, Directive, ElementRef, inject, input } from '@angular/core';

export type ButtonSize = 'sm' | 'md' | 'lg';

const BASE =
  'inline-flex cursor-pointer items-center justify-center rounded-xl bg-accent-500 font-semibold ' +
  'text-white shadow-lg transition-colors hover:bg-accent-600 focus-visible:outline-2 ' +
  'focus-visible:outline-offset-2 focus-visible:outline-accent-400';

const DISABLED = 'pointer-events-none cursor-not-allowed opacity-60 shadow-none';

const SIZE: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm',
  md: 'px-5 py-2 text-sm sm:px-6 sm:py-2.5 sm:text-base',
  lg: 'px-6 py-3 text-base sm:px-8 sm:py-3.5 sm:text-xl',
};

/**
 * Shared primary button. Apply to a native `<a>` or `<button>` so routing,
 * `type="submit"` and disabled state keep working:
 *
 *   <a appButton size="lg" routerLink="/plan">Plan your trip</a>
 *   <button appButton type="submit" [disabled]="form.invalid">Save</button>
 *   <a appButton [disabled]="!ready">Continue</a>
 *
 * Extra classes on the element (e.g. layout margins) are merged with the
 * button styles, so `class="mt-9"` still applies.
 */
@Directive({
  selector: 'a[appButton], button[appButton]',
  host: {
    '[class]': 'classes()',
    '[attr.disabled]': 'nativeDisabled()',
    '[attr.aria-disabled]': 'disabled() || null',
    '[attr.tabindex]': 'isAnchor && disabled() ? -1 : null',
    '(click)': 'onClick($event)',
  },
})
export class Button {
  protected readonly isAnchor =
    inject(ElementRef).nativeElement.tagName.toLowerCase() === 'a';

  readonly size = input<ButtonSize>('md');
  readonly disabled = input(false, { transform: booleanAttribute });

  protected readonly classes = computed(
    () => `${BASE} ${SIZE[this.size()]}${this.disabled() ? ` ${DISABLED}` : ''}`,
  );

  // A native `disabled` attribute only applies to <button>, not <a>.
  protected readonly nativeDisabled = computed(() =>
    !this.isAnchor && this.disabled() ? '' : null,
  );

  protected onClick(event: Event): void {
    if (this.disabled()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}
